import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { USER_ROLES } from "../const";

// Skeleton Loader Component
const SkeletonLoader = () => (
  <div className="min-h-screen bg-gray-50 p-4">
    {/* Navbar Skeleton */}
    <div className="animate-pulse">
      <div className="bg-gray-200 h-16 rounded-lg mb-6"></div>

      {/* Main Content Skeleton */}
      <div className="max-w-7xl mx-auto">
        {/* Page Header Skeleton */}
        <div className="mb-8">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>

        {/* Content Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-100"
            >
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-5/6 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3 mt-4"></div>
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="mt-8">
          <div className="h-10 bg-gray-200 rounded-t-lg mb-1"></div>
          {[1, 2, 3, 4, 5].map((row) => (
            <div
              key={row}
              className="h-12 bg-white border-b border-gray-100 flex items-center"
            >
              <div className="h-4 bg-gray-100 rounded w-1/4 mx-4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/6 ml-auto mr-4"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ProtectedRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!isAuthenticated) {
    if (location.pathname === "/top-rfps") {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const role = user?.role?.toLowerCase?.();
  const path = location.pathname || "";

  if (role === USER_ROLES.SUPER_ADMIN) {
    // Allow Super Admin to access everything except regular admin routes they shouldn't see

    if (!path.startsWith("/super-admin") && path !== "/user-profile") {
      // Only redirect if not on super-admin page and not on user-profile
      return <Navigate to="/super-admin" replace />;
    }
  }

  // Admin access control
  if (path.startsWith("/admin")) {
    if (role !== USER_ROLES.ADMIN) {
      // Only admins can access admin pages
      return (
        <Navigate
          to={role === USER_ROLES.SUPER_ADMIN ? "/super-admin" : "/"}
          replace
        />
      );
    }
  }

  // Role-based redirections for admin/super-admin users
  if (role === USER_ROLES.SUPER_ADMIN) {
    // Allow Super Admin to access everything except regular admin routes they shouldn't see
    // But specifically allow user-profile
    if (!path.startsWith("/super-admin") && path !== "/user-profile") {
      // Only redirect if not on super-admin page and not on user-profile
      return <Navigate to="/super-admin" replace />;
    }
  } else if (role === USER_ROLES.ADMIN) {
    // Admin users: restrict to admin pages and allowed member pages
    if (
      !path.startsWith("/admin") &&
      path !== "/feedback-reports" &&
      path !== "/" &&
      path !== "/top-rfps" &&
      path !== "/targeting-logic" &&
      path !== "/targeting" &&
      !path.startsWith("/targeting/") &&
      path !== "/recipes" &&
      !path.startsWith("/recipes/") &&
      path !== "/billing" &&
      path !== "/user-profile" &&
      !path.startsWith("/report/") &&
      path !== "/groups" &&
      !path.startsWith("/groups/") &&
      !path.startsWith("/rfp/expanded/")
    ) {
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
